import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canManageHeating, canManageKiosk, canManageWorkDuty, isUserAdmin } from "../utils/userUtils";
import '../styles/Navbar.css'; // responsive Navbar Styles

function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const firstLinkRef = useRef(null);
  const toggleMenu = () => setOpen(o => !o);
  const closeMenu = () => {
    setOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  // Body Scroll Lock & Fokus auf erstes Element (nur auf Mobile)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (open && isMobile) {
      document.body.classList.add('no-scroll');
      // Fokus nach Öffnen
      setTimeout(() => { firstLinkRef.current?.focus(); }, 50);
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [open]);

  // Escape schließt Menü
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeMenu(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const renderDropdown = (title, items) => {
    if (items.length === 1) {
      return (
        <li className="navbar-item" role="none">
          <Link to={items[0].to} className="navbar-link" role="menuitem" onClick={closeMenu}>{items[0].label}</Link>
        </li>
      );
    }
    return (
      <li className={`navbar-item navbar-dropdown ${openDropdown === title ? 'is-open' : ''}`} role="none">
          <button
              className="navbar-link"
              onClick={() => toggleDropdown(title)}
              aria-haspopup="true"
              aria-expanded={openDropdown === title}
          >
              {title}
          </button>
          <ul className="dropdown-menu" role="menu">
              {items.map((item, index) => (
                  <li key={index} role="none">
                      <Link to={item.to} className="dropdown-link" role="menuitem" onClick={closeMenu}>{item.label}</Link>
                  </li>
              ))}
          </ul>
      </li>
    );
  };

  return (
    <nav className="navbar" role="navigation" aria-label="Hauptnavigation">
      <div className="navbar-row">
        <Link to="/" className="navbar-brand" onClick={closeMenu}>Tennis</Link>
        <button
          className={"navbar-toggle" + (open ? " open" : "")}
          type="button"
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-controls="hauptmenue"
          aria-expanded={open}
          onClick={toggleMenu}
        >
          <span className="hamburger" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="sr-only">{open ? "Menü schließen" : "Menü öffnen"}</span>
        </button>
        <ul id="hauptmenue" className={"navbar-menu" + (open ? " open" : "")} role="menubar">
          <li className="navbar-item" role="none"><Link ref={firstLinkRef} to="/" className="navbar-link" role="menuitem" onClick={closeMenu}>Home</Link></li>

          {renderDropdown("Turniere", [
              { to: "/tournaments", label: "Turniere & Anmeldung" },
              ...(isUserAdmin(user) ? [{ to: "/tournaments/manage", label: "Verwalten" }] : [])
          ])}
          <li className="navbar-item" role="none"><Link to="/mannschaften" className="navbar-link" role="menuitem" onClick={closeMenu}>Mannschaften</Link></li>
          <li className="navbar-item" role="none"><Link to="/news" className="navbar-link" role="menuitem" onClick={closeMenu}>News</Link></li>
<li className="navbar-item" role="none"><Link to="/getraenke" className="navbar-link" role="menuitem" onClick={closeMenu}>Getränke</Link></li>
          {user && <li className="navbar-item" role="none"><Link to="/bock" className="navbar-link" role="menuitem" onClick={closeMenu}>Bock-List</Link></li>}
          {user && <li className="navbar-item" role="none"><Link to="/arbeitsdienst" className="navbar-link" role="menuitem" onClick={closeMenu}>Arbeitsdienst</Link></li>}
          {user && <li className="navbar-item" role="none"><Link to="/vereinstermine" className="navbar-link" role="menuitem" onClick={closeMenu}>Vereinstermine</Link></li>}
          {isUserAdmin(user) && <li className="navbar-item" role="none"><Link to="/calendar" className="navbar-link" role="menuitem" onClick={closeMenu}>Meine Termine</Link></li>}
          {(isUserAdmin(user) || canManageKiosk(user)) &&
          renderDropdown("Organisation", [
              ...(isUserAdmin(user) ? [{ to: "/bookings", label: "Buchungen" }] : []),
              ...(isUserAdmin(user) ? [{ to: "/tasks", label: "Aufgaben" }] : []),
              ...(isUserAdmin(user) ? [{ to: "/appointments", label: "Termine" }] : []),
              ...(canManageKiosk(user) ? [{ to: "/kiosk/config", label: "Kiosk" }] : [])
          ])}

          {(isUserAdmin(user) || canManageHeating(user) || canManageWorkDuty(user)) && renderDropdown("Admin", [
              ...(isUserAdmin(user) ? [{ to: "/admin", label: "Verwaltung" }] : []),
              ...(canManageHeating(user) ? [{ to: "/heating-plan", label: "Heizplan" }] : []),
              ...(canManageWorkDuty(user) ? [{ to: "/arbeitsdienst/konten", label: "Arbeitsdienst-Konten" }] : [])
          ])}

          <li className="navbar-item" role="none"><a href="https://tennis-thedinghausen.fan12.de/" target="_blank" rel="noreferrer" className="navbar-link" role="menuitem" onClick={closeMenu}>Fanshop</a></li>
          <li className="navbar-item" role="none"><Link to="/about" className="navbar-link" role="menuitem" onClick={closeMenu}>Über uns</Link></li>

          {user && (
            <li className="navbar-item" role="none"><Link to="/profile" className="navbar-link" role="menuitem" onClick={closeMenu}>Profil</Link></li>
          )}

          <li className="navbar-item navbar-item--auth" role="none">
            {user ? (
              <button
                className="navbar-button"
                role="menuitem"
                onClick={() => { logout(); closeMenu(); }}
              >Logout ({user.username})</button>
            ) : (
              <Link to="/login" className="navbar-link" role="menuitem" onClick={closeMenu}>Login</Link>
            )}
          </li>
        </ul>
      </div>
      {open && window.innerWidth <= 768 && <div className="navbar-overlay" onClick={closeMenu} aria-hidden="true"/>}
    </nav>
  );
}

export default Navbar;
